<template>
  <UsageExample v-model="tab" :code="code" :tabs="tabs">
    <v-row>
      <v-col>
        <hx-table
          :title="title"
          :items="items"
          :headers="headers"
          :loading="loading"
          :show-toolbar="showToolbar"
          :show-search="showSearch"
          :show-select="showSelect"
          :show-header="showHeader"
          :show-footer="showFooter"
          :items-per-page="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          :class="`${showScroll ? 'fix-height' : ''}`"
          @filter-click="handleFilterClick"
        >
          <template #[`cell.button`]="{ item, header }">
            <hx-button
              :left-icon="item.buttonLeftIcon"
              :label="item.buttonLabel"
              variant="primary"
              size="large"
              ghost
              @click="handleActionClick(item, header)"
            />
          </template>

          <template #[`cell.buttons`]="{ item, header }">
            <span class="cell-grouped-actions">
              <hx-button
                v-for="(button, index) in item.rowActions"
                :key="index"
                :left-icon="button.leftIcon"
                variant="secondary"
                size="large"
                ghost
                @click="handleGroupedActionClick(item, header, button, index)"
              />
            </span>
          </template>

          <template #[`cell.status`]="{ item }">
            <hx-chip :label="item.status" :color="item.chipColor" type="static" />
          </template>

          <template #[`cell.progress`]="{ item }">
            <hx-linear-progress
              :value="item.progressValue"
              :total="item.progressTotal"
              :state="item.progressState"
              unit="Tasks"
              :show-helper-text="false"
            />
          </template>
        </hx-table>
      </v-col>
    </v-row>

    <template #configuration>
      <v-switch
        v-model="loading"
        :label="`Loading: ${loading.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showSelect"
        :label="`Show Select Button: ${showSelect.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showTitle"
        :label="`Show title: ${showTitle.toString()}`"
        color="primary"
        @change="toggleTitle"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showToolbar"
        :label="`Show toolbar: ${showToolbar.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showSearch"
        :label="`Show search: ${showSearch.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showHeader"
        :label="`Show header: ${showHeader.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showFooter"
        :label="`Show footer: ${showFooter.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-switch
        v-model="showScroll"
        :label="`Show scrollbar: ${showScroll.toString()}`"
        color="primary"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-select
        v-model="totalItems"
        label="Total items"
        :items="['0', '5', '10', '25', '100']"
        max-width="200"
        class="mt-4"
        color="primary"
        variant="outlined"
      ></v-select>
    </template>
  </UsageExample>
</template>

<script>
import UsageExample from '@/components/helix/utils/UsageExample';

export default {
  name: 'HelixTable',
  components: { UsageExample },
  data() {
    return {
      tab: 'Table Usage',
      tabs: ['Table Usage'],
      title: undefined,
      totalItems: 100,
      loading: false,
      showTitle: false,
      showSelect: false,
      showToolbar: false,
      showSearch: true,
      showHeader: true,
      showFooter: true,
      showScroll: false,
      itemsPerPage: 10,
      itemsPerPageOptions: [10, 20, 30],
      headers: [
        {
          title: 'Portal',
          value: 'portal',
          key: 'portal',
          minWidth: 275,
          sortable: true
        },
        { title: 'Title', value: 'title', key: 'title', minWidth: 350, sortable: true },
        { title: 'Type', value: 'type', key: 'type' },
        {
          title: 'Learning Object ID',
          value: 'learningObjectID',
          key: 'learningObjectID',
          minWidth: 175
        },
        { title: 'Assigned', value: 'assigned', key: 'assigned' },
        { title: 'Pending', value: 'pending', key: 'pending' },
        { title: 'Not Started', value: 'notStarted', key: 'notStarted' },
        {
          title: 'Button',
          key: 'button'
        },
        {
          title: 'Buttons',
          key: 'buttons'
        },
        {
          title: 'Status',
          key: 'status',
          value: 'status',
          sortable: true
        },
        {
          title: 'Progress',
          key: 'progress'
        }
      ]
    };
  },
  computed: {
    code() {
      return `<hx-table
        :title="title"
        :items="items"
        :headers="headers"
        :loading="loading"
        :show-toolbar="showToolbar"
        :show-search="showSearch"
        :show-select="showSelect"
        :show-header="showHeader"
        :show-footer="showFooter"
        :items-per-page="itemsPerPage"
        :items-per-page-options="itemsPerPageOptions"
        @filter-click="handleFilterClick"
      >
        <!-- One #cell.<key> slot per column, where <key> is the column's key.
             Omit the slot and the column renders its plain value. Destructure
             header to recover the argument the removed single-action-click and
             grouped-actions-click events used to pass. -->
        <template #cell.button="{ item, header }">
          <hx-button
            :left-icon="item.buttonLeftIcon"
            :label="item.buttonLabel"
            variant="primary"
            size="large"
            ghost
            @click="handleActionClick(item, header)"
          />
        </template>

        <template #cell.buttons="{ item, header }">
          <span class="cell-grouped-actions">
            <hx-button
              v-for="(button, index) in item.rowActions"
              :key="index"
              :left-icon="button.leftIcon"
              variant="secondary"
              size="large"
              ghost
              @click="handleGroupedActionClick(item, header, button, index)"
            />
          </span>
        </template>

        <template #cell.status="{ item }">
          <hx-chip :label="item.status" :color="item.chipColor" type="static" />
        </template>

        <template #cell.progress="{ item }">
          <hx-linear-progress
            :value="item.progressValue"
            :total="item.progressTotal"
            :state="item.progressState"
            unit="Tasks"
            :show-helper-text="false"
          />
        </template>

        <!-- Replaces the toolbar's default Filter button entirely, so
             @filter-click no longer fires. Omit it to keep that button. -->
        <template #toolbar-actions>
          <hx-button label="Export" right-icon="mdi-download-outline" @click="exportRows" />
        </template>
      </hx-table>

// Besides item, each #cell.<key> slot forwards value, column, index, internalItem,
// isSelected, toggleSelect, isExpanded, toggleExpand, and header.

// Data structure for headers prop
const headers = [
  {
    title: 'Portal',
    value: 'portal',
    key: 'portal',
    minWidth: 275,
    sortable: true
  },
  // A column rendered through a slot carries no value: the slot reads the row
  // directly, and a column whose value resolves to nothing stays out of the
  // toolbar search. Set align to centre such a column's header and cells.
  {
    title: 'Button',
    key: 'button'
  },
  {
    title: 'Buttons',
    key: 'buttons'
  },
  {
    title: 'Status',
    key: 'status',
    value: 'status',
    sortable: true
  },
  {
    title: 'Progress',
    key: 'progress'
  }
];

// Data structure for items prop. These fields are ordinary data that the
// slots above read: HxTable itself no longer looks at any of them. Keep
// non-string data such as rowActions under a name no column points at,
// because the toolbar search stringifies whatever a column resolves to.
const items = [
  {
    id: index,
    portal: 'Example Portal', 
    title: 'Example Title',
    type: 'Example Type',
    learningObjectID: 'Example ID',
    assigned: index,
    pending: 0,
    notStarted: 0,
    buttonLeftIcon: 'mdi-plus-circle-outline', // mdi icons are supported
    buttonLabel: 'Button',
    rowActions: [
      {
        leftIcon: 'mdi-pencil-outline' // mdi icons are supported
      },
      {
        leftIcon: 'mdi-delete-outline' // mdi icons are supported
      }
    ],
    status: 'Success', // Should follow convention of HxChip component
    chipColor: 'green', // Should follow convention of HxChip component
    progressValue: 75,
    progressTotal: 100,
    progressState: 'warning' // Should follow convention of HxLinearProgress component
  }
]
`;
    },
    items() {
      return [...Array(Number(this.totalItems))].map((title, index) => {
        return {
          id: index,
          portal: `aacg-complispacelearn.mygo1.com ${index}`,
          title: `About Me - New Standards - Introduction ${index}`,
          type: `Video${index}`,
          learningObjectID: `92381234${index}`,
          assigned: index,
          pending: 0,
          notStarted: 0,
          buttonLeftIcon: 'mdi-plus-circle-outline',
          buttonLabel: 'Button',
          rowActions: [
            {
              leftIcon: 'mdi-pencil-outline'
            },
            {
              leftIcon: 'mdi-delete-outline'
            }
          ],
          status: index % 2 === 0 ? 'Success' : 'Error',
          chipColor: index % 2 === 0 ? 'green' : 'red',
          progressValue: index % 2 === 0 ? 75 : 25,
          progressTotal: 100,
          progressState: index % 3 === 0 ? 'ok' : index % 2 === 0 ? 'error' : 'warning'
        };
      });
    }
  },
  methods: {
    toggleTitle() {
      if (this.showTitle) {
        this.title = 'Helix Table Title';
      } else {
        this.title = undefined;
      }
    },
    handleActionClick(item, header) {
      console.log('Cell button clicked:', item, header);
    },
    handleGroupedActionClick(item, header, button, index) {
      console.log('Cell buttons clicked:', item, header, button, index);
    },
    handleFilterClick() {
      console.log('Toolbar filter clicked');
    }
  }
};
</script>

<style scoped>
:deep(.v-container.fix-height .hx-table .v-table__wrapper) {
  height: 405px;
}

/* Replaces the gap the removed hx-table-row-cell-buttons class used to provide. */
.cell-grouped-actions {
  display: inline-flex;
  gap: 8px;
}
</style>
